import { EstadoPatrimonial as EstadoPatrimonialModel } from "../../models/trust/common/EstadoPatrimonial";
import * as generic from "../Generico";
import { useEffect, useRef, useState } from "react";
import { EstadoPatrimonialService } from "../../service/trust/EstadoPatrimonialService";
import { useLazyParams } from "../../hooks/useLazyParams";
import { useForm } from "react-hook-form";
import { InputComponent } from "../InputComponent";
import { confirmDialog } from "primereact/confirmdialog";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { Menu } from "primereact/menu";
import { TablaDinamica } from "../TablaDinamica";
import { Dialog } from "primereact/dialog";

export const EstadoPatrimonial = ({ toast, id, tipo, catalogos, permss, soloLectura }) => {
    // Inicialización
    const omodel = new EstadoPatrimonialModel();
    if (tipo === 'clientePj') {
        omodel.model.fecha_nacimiento.table = true; // Activar columna de fecha para la tabla
    }
    const defaultValues = generic.funDefault(omodel.model, null);
    // Estados principales
    const [dialogAccionista, setDialogAccionista] = useState(false);
    const [accionistaSeleccionado, setAccionistaSeleccionado] = useState(null);
    const [esRuc, setEsRuc] = useState(false);
    const [esCedula, setEsCedula] = useState(false);
    const menuRef = useRef(null);
    // Servicios y Hooks
    const estadoPatSrv = new EstadoPatrimonialService();
    const tablaEstado = useLazyParams({ modelo: omodel.model });
    const { control, formState: { errors }, handleSubmit, reset, setError, getValues, clearErrors } = useForm({ defaultValues });

    const { inNumber, inText, inDrop, inCalendar } = InputComponent({ omodel, errors, control, permss, id: getValues('id'), soloLectura });

    useEffect(() => {
        if (id) {
            loadLazyData();
        }
        // eslint-disable-next-line
    }, [tablaEstado.lazyParams, id]);

    const loadLazyData = () => {
        tablaEstado.setLoading(true);
        let _lazyParams = { ...tablaEstado.lazyParams };
        _lazyParams.idp = id;
        _lazyParams.tipo = tipo;
        estadoPatSrv.getLista({ lazyEvent: JSON.stringify(_lazyParams) }).then(res => {
            tablaEstado.setTotalRecords(res.data.total);
            tablaEstado.setData(res.data.registros);
        }).finally(() => {
            tablaEstado.setLoading(false);
        });
    };

    const openNew = () => {
        reset(defaultValues);
        setDialogAccionista(true);
    };

    const hideDialog = () => setDialogAccionista(false);

    const onSubmit = (data) => {
        data.idp = id;
        data.tipo = tipo;
        data.fecha_nacimiento = generic.dateToString(data.fecha_nacimiento);
        const request = getValues('id') == null ? estadoPatSrv.nuevo(data) : estadoPatSrv.editar(data);

        request.then(res => {
            setDialogAccionista(false);
            loadLazyData();
            generic.funcSuccess(res, toast);
        }).catch(error => {
            generic.funcError(error, toast, setError);
        });
    };

    const editFicha = (idAccionista) => {
        estadoPatSrv.consultar(idAccionista).then(res => {
            const cats = catalogos;
            let _accionista = res.data.accionista;
            setEsRuc(JSON.stringify(_accionista.tipo_identificacion_rep) === JSON.stringify(cats?.ruc));
            setEsCedula(JSON.stringify(_accionista.tipo_identificacion_rep) === JSON.stringify(cats?.cedula));
            _accionista.fecha_nacimiento = generic.stringToDate(_accionista.fecha_nacimiento);
            reset(generic.renewValues(_accionista, omodel.model));
            setDialogAccionista(true);
        });
    };

    const confirmDelete = (idAccionista) => {
        confirmDialog({
            message: '¿Está seguro de eliminar este accionista/socio?',
            header: 'Confirmación',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                estadoPatSrv.delete(idAccionista).then(res => {
                    loadLazyData();
                    generic.funcSuccess(res, toast);
                }).catch(error => generic.funcDeleteError(error, toast));
            }
        });
    };

    const onChangeTipoIdentificacion = (e) => {
        setEsRuc(JSON.stringify(e.value) === JSON.stringify(catalogos?.ruc));
        setEsCedula(JSON.stringify(e.value) === JSON.stringify(catalogos?.cedula));
        clearErrors('tipo_identificacion');
    }

    const itemsMenu = () => {
        if (soloLectura) {
            return [{
                label: 'Opciones',
                items: [
                    { label: 'Ver detalle', icon: 'pi pi-eye', command: () => editFicha(accionistaSeleccionado?.id) }
                ]
            }];
        }

        return [{
            label: 'Opciones',
            items: [
                { label: 'Editar', icon: 'pi pi-pencil', command: () => editFicha(accionistaSeleccionado?.id) },
                { label: 'Eliminar', icon: 'pi pi-trash', command: () => confirmDelete(accionistaSeleccionado?.id) }
            ]
        }];
    };

    const accionesTemplate = (rowData) => (
        <Button icon="pi pi-caret-down" type="button" className="p-button-rounded p-button-success mr-2" title="Opciones/Acciones" aria-controls="popup_menu" aria-haspopup
            onClick={(event) => { setAccionistaSeleccionado(rowData); menuRef.current.toggle(event); }}
        />
    );

    const ColumnaAcciones = (
        <Column header="Acciones" body={accionesTemplate} style={{ width: '4rem', textAlign: 'center' }} frozen alignFrozen="right" />
    );

    const cabeceraTabla = () => {
        return (
            <div className="flex align-items-center justify-content-start gap-3 py-1">
                {permss?.crear && id && !soloLectura && (
                    <Button icon="pi pi-plus" className="p-button-success p-button-sm" type="button" onClick={openNew} />
                )}
                <span className="text-sm font-semibold text-600 uppercase tracking-wider">
                    Listado de Accionistas / Socios
                </span>
            </div>
        );
    };

    const tipoDocRep = esRuc ? 'IDEN_RUC' : esCedula ? 'IDEN_CEDULA' : null;
    const reglaValidacionIden = (val) => {
        if (!tipoDocRep) return true;
        return generic.validarIdentificacionEcuador(val, tipoDocRep) || `${esRuc ? 'RUC' : 'Cédula'} incorrecto`;
    };

    return (
        <div className="componente-estado-patrimonial">
            <Menu model={itemsMenu()} popup ref={menuRef} id="popup_menu" />
            <TablaDinamica modelo={omodel.model} estadoTabla={tablaEstado} datos={tablaEstado.data} totalRegistros={tablaEstado.totalRecords} dataKey="id" acciones={ColumnaAcciones} header={cabeceraTabla()} />

            <Dialog visible={dialogAccionista} header={getValues('id') == null ? "Nuevo Accionista/Socio" : "Editar Accionista/Socio"} modal className="p-fluid modal-ssize dialog-personalizado" onHide={hideDialog} style={{ width: '600px' }}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="formgrid grid px-2">

                        <div className="col-12 mb-3">
                            <div className="flex align-items-center">
                                <span className="text-800 font-bold text-base mr-3 flex align-items-center gap-2">
                                    <i className="pi pi-users text-primary"></i>
                                    ESTADO PATRIMONIAL (ACCIONISTAS / SOCIOS):
                                </span>
                                <div className="border-bottom-1 border-300 flex-grow-1" style={{ height: '1px' }}></div>
                            </div>
                        </div>

                        {inText('nombre_accionista', { icon: 'pi pi-user' }, 12)}
                        {inDrop('tipo_identificacion', catalogos?.tipo_identificacion, { lab: omodel.optionLabel, chn: onChangeTipoIdentificacion }, 6)}
                        {inText('identificacion', { validate: reglaValidacionIden, icon: 'pi pi-id-card' }, 6)}
                        {inNumber('participacion', 2, false, { suffix: ' %', placeholder: 'Ej. 25.50' }, 6)}
                        {tipo === 'clientePj' && inCalendar('fecha_nacimiento', { icon: 'pi pi-user', disabled: soloLectura }, 6)}
                        <div className="col-12 mt-4 flex justify-content-end">
                            {soloLectura ? (
                                <div className="field col">
                                    <Button label="Cerrar" style={{ maxWidth: '120px' }} icon="pi pi-times" type="button" className="p-button-warning" onClick={() => hideDialog(false)} />
                                </div>
                            ) : (
                                generic.buttonsForms(hideDialog)
                            )}
                        </div>
                    </div>
                </form>
            </Dialog>
        </div>
    );
};